import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AutoSaveForm } from "@/components/forms/AutoSaveForm";
import { useAppToast } from "@/hooks/useAppToast";
import { getModule, updateModule } from "@/services/admin/modules.service";
import { listExercisesByModule } from "@/services/admin/exercises.service";
import {
  Module,
  UpdateModuleInputs,
} from "@/services/admin/modules.interfaces";
import { Card } from "@/components/ui/card";
import { ModuleExercises } from "./ModuleExercises";
import { useLoading } from "@/lib/LoadingHook";
import { ExerciseProvider } from "./exercises/context";
import { toKebabCase, isValidKebabCase } from "@/utils/stringNormalization";

interface ModuleInfoFormProps {
  moduleData: Module;
  onSave: (data: UpdateModuleInputs) => Promise<void>;
}

const ModuleInfoForm: React.FC<ModuleInfoFormProps> = ({
  moduleData,
  onSave,
}) => {
  return (
    <div className="mb-8">
      <AutoSaveForm onSave={onSave} className="space-y-4">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Módulo</Label>
              <Input
                id="name"
                name="name"
                type="text"
                defaultValue={moduleData.name}
                onBlur={(e) => {
                  // Auto-preenchimento do internalName apenas uma vez
                  const internalNameField = document.getElementById('internalName') as HTMLInputElement;
                  if (internalNameField && !internalNameField.value) {
                    internalNameField.value = toKebabCase(e.target.value);
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="internalName">Nome Interno</Label>
              <Input
                id="internalName"
                name="internalName"
                type="text"
                defaultValue={moduleData.internalName || ''}
                placeholder="ex: modulo-basico-1"
                onBlur={(e) => {
                  const normalized = toKebabCase(e.target.value);
                  e.target.value = normalized;
                  
                  // Mostrar warning se não está em kebab-case válido
                  if (e.target.value && !isValidKebabCase(normalized)) {
                    console.warn('Nome interno deve estar em formato kebab-case (ex: modulo-basico-1)');
                  }
                }}
              />
              <p className="text-sm text-muted-foreground">
                Nome interno para URLs e identificação. Será normalizado automaticamente para kebab-case.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                name="title"
                type="text"
                defaultValue={moduleData.title}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                name="description"
                className="h-32"
                defaultValue={moduleData.description}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="summarizedDescription">Descrição Resumida</Label>
              <Textarea
                id="summarizedDescription"
                name="summarizedDescription"
                className="h-24"
                defaultValue={moduleData.summarizedDescription}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="teaser">Teaser</Label>
              <Textarea
                id="teaser"
                name="teaser"
                className="h-24"
                defaultValue={moduleData.teaser}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                name="status"
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                defaultValue={moduleData.status}
              >
                <option value="Draft">Rascunho</option>
                <option value="Published">Publicado</option>
                <option value="Archived">Arquivado</option>
              </select>
            </div>
          </div>
        </Card>
      </AutoSaveForm>
    </div>
  );
};

const AdminEditModulePage: React.FC = () => {
  const { moduleId } = useParams<{
    moduleId: string;
  }>();
  const toast = useAppToast();
  const { isLoading: isModuleLoading, whenLoading: whenModuleLoading } =
    useLoading();
  const { isLoading: isExercisesLoading, whenLoading: whenExercisesLoading } =
    useLoading();
  const [moduleData, setModuleData] = useState<Module | null>(null);
  const [exercisesError, setExercisesError] = useState<string | null>(null);

  useEffect(() => {
    const loadModule = async () => {
      console.log(
        "Loading module with moduleId:",
        moduleId
      );
      if (!moduleId) {
        console.log("Missing moduleId");
        return;
      }

      await whenModuleLoading(async () => {
        try {
          const data = await getModule(moduleId);
          setModuleData(data);
        } catch (error) {
          toast.error("Falha ao carregar dados do módulo");
          console.error("Error loading module:", error);
        }
      });
    };

    const loadExercises = async () => {
      console.log(
        "Loading exercises with moduleId:",
        moduleId
      );
      if (!moduleId) {
        console.log("Missing moduleId");
        return;
      }

      await whenExercisesLoading(async () => {
        try {
          console.log("Before fetching exercises");
          await listExercisesByModule(moduleId);
          console.log("Exercises loaded");
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          setExercisesError(`Falha ao carregar exercícios: ${errorMessage}`);
          console.error("Error loading exercises:", error);
        }
      });
    };

    loadModule();
    loadExercises();
  }, [moduleId]);

  const handleSave = async (changes: UpdateModuleInputs) => {
    if (!moduleId) return;

    try {
      await updateModule(moduleId, changes);
      setModuleData((prev) => (prev ? { ...prev, ...changes } : null));
      toast.success("Alterações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar alterações.");
      console.error("Error updating module:", error);
      throw error;
    }
  };

  if (isModuleLoading) {
    return (
    <AdminPageLayout title="Carregando...">
      <div className="flex items-center justify-center py-8">Carregando...</div>
    </AdminPageLayout>
    );
  }

  if (isExercisesLoading) {
    return (
    <AdminPageLayout title="Carregando Exercícios...">
      <div className="flex items-center justify-center py-8">
        Carregando exercícios...
      </div>
    </AdminPageLayout>
    );
  }

  if (exercisesError) {
    return (
      <AdminPageLayout title="Erro">
        <div className="flex items-center justify-center py-8">
          {exercisesError}
        </div>
      </AdminPageLayout>
    );
  }

  if (!moduleData) {
    return (
      <AdminPageLayout title="Módulo Não Encontrado">
        <div className="flex items-center justify-center py-8">
          Módulo não encontrado
        </div>
      </AdminPageLayout>
    );
  }

  const BackButtons = () => {
    return (
      <div className="flex gap-4 mb-4">
        <Link
          to="/admin/modules"
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Módulos
        </Link>
      </div>
    );
  };

  return (
    <AdminPageLayout title={`Editar ${moduleData.name}`}>
      <BackButtons />
      <main id="main-content" className="mx-auto py-8 space-y-8">
        <ModuleInfoForm moduleData={moduleData} onSave={handleSave} />

        <ExerciseProvider 
          moduleId={moduleId || ""} 
        >
          <ModuleExercises />
          {moduleId && (
            <Card className="p-6 mt-8">
              <h3 className="text-lg font-semibold mb-2">Pool do teste do módulo</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure quais exercícios podem cair no teste deste módulo (criar, editar e reordenar) na área dedicada.
              </p>
              <Link
                to={`/admin/educational/module-tests/${moduleId}`}
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                Configurar pool em Testes de módulo →
              </Link>
            </Card>
          )}
        </ExerciseProvider>
      </main>
    </AdminPageLayout>
  );
};

export default AdminEditModulePage;